import { Autocomplete, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import Grid from "@mui/material/Grid2";
import SnoozeIcon from "@mui/icons-material/Snooze";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import SearchIcon from "@mui/icons-material/Search";
import InsightsFiltersCard from "./InsightsFiltersCard";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { setSelectedBusinessInsight, setSelectedRows } from '../../../../../features/businessInsightsSlice';
import { CheckPermissions } from '../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions';
import AddToPhotosOutlinedIcon from "@mui/icons-material/AddToPhotosOutlined";
import { selectUserData } from '../../../../../features/userSlice';

interface Props {
  onResolveIconClick: (resolveIconType: string) => void;
  onAssignIconClick: (assignIconType: string) => void;
  onSnoozeIconClick: (snoozeIconType: string) => void;
}

const InsightsFilters = ({ onResolveIconClick, onAssignIconClick, onSnoozeIconClick }: Props) => {
  const dispatch = useDispatch();
  const response = useSelector(selectUserData); // Get user data from Redux store
  const filteredInsights = useSelector((state: any) => state.businessInsights?.filteredInsights); // Access filtered insights from Redux store
  const categoriesToKeep = ['User Acquisition', 'Player Value']; // Define the categories to keep
  const requiredInsights = filteredInsights?.filter((task: any) => {
    const categody = task?.data && task?.data['query_output:category'] && task?.data['query_output:category'].value;
    return categoriesToKeep.includes(categody);
  });
  const selectedBusinessInsight = useSelector((state: any) => state.businessInsights?.selectedBusinessInsight);
  const selectedRows = useSelector((state: any) => state.businessInsights?.selectedRows);
  const [searchParams, setSearchParams] = useSearchParams();

  const clearSelectedBusinessInsights = () => {
    dispatch(setSelectedBusinessInsight(null));
    dispatch(setSelectedRows([])); // Clear selected rows when clearing business insights
  };

  //-- this hold search params in url
  useEffect(() => {
    // Update selectedTemplate when searchParams change (e.g., on back/forward navigation)
    dispatch(setSelectedBusinessInsight(searchParams.get("search")));
    // eslint-disable-next-line
  }, []);

  const hasEditBusinessInsightsPermission = CheckPermissions(response?.data?.products?.paid_traffic, "performance_analysis", "edit_paid_traffic_insights"); // Check if the user has permission to view business insights

  return (
    <>
      <Box sx={{ padding: "8px 16px" }}>
        <Box>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid size={8}>
              <Typography variant="h3" sx={{ fontSize: "24px" }}>
                Insights List
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontSize: "14px" }}
                color="rgba(0, 0, 0, 0.6)"
              >
                Uncover insights, translate data into value, promote alignment,
                and fuel strategic actions
              </Typography>
            </Grid>
            <Grid
              size={4}
              container
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="flex-end"
            >
              <Grid>
                <Typography>Actions:</Typography>
              </Grid>
              {hasEditBusinessInsightsPermission ? (
                <>
                  <Grid>
                    <Tooltip title="Change Status" placement="top">
                      <IconButton aria-label="delete" color="secondary" disabled={selectedRows && selectedRows.length === 0} onClick={() => onSnoozeIconClick("snooze")}>
                        <SnoozeIcon data-tracker="119" color={selectedRows.length > 0 ? "secondary" : "disabled"} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid>
                    <Tooltip title="Assignee" placement="top">
                      <IconButton aria-label="collection" color="secondary" disabled={selectedRows && selectedRows.length === 0} onClick={() => onAssignIconClick("assign")}>
                        <SwitchAccountIcon
                          data-tracker="303"
                          color={selectedRows.length > 0 ? "secondary" : "disabled"}
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid>
                    <Tooltip title="Resolve" placement="top">
                      <IconButton aria-label="check" disabled={selectedRows && selectedRows.length === 0} onClick={() => onResolveIconClick("resolve")}>
                        <CheckCircleOutlineOutlinedIcon
                          data-tracker="302"
                          color={selectedRows.length > 0 ? "secondary" : "disabled"}
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid>
                    <Tooltip title="Snooze" placement="top">
                      <IconButton
                        aria-label="delete"
                        color="secondary"
                        disabled
                      >
                        <SnoozeIcon color={"disabled"} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid>
                    <Tooltip title="Assignee" placement="top">
                      <IconButton
                        aria-label="collection"
                        color="secondary"
                        disabled
                      >
                         <AddToPhotosOutlinedIcon color={"disabled"} data-tracker="302" />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid>
                    <Tooltip title="Resolve" placement="top">
                      <IconButton aria-label="check" disabled data-tracker="302">
                        <CheckCircleOutlineOutlinedIcon color={"disabled"} data-tracker="302" />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Box>
        <Box>
          <Stack
            mt={0}
            mb={1}
            justifyContent={"space-between"}
            alignItems={"center"}
            direction="row"
            gap={2}
          >
            <Stack
              direction="row"
              spacing={2}
              sx={{
                overflowX: "auto",
                whiteSpace: "nowrap",
                display: "flex",
                "@media (max-width: 1800px)": {
                  width: "62%", // Change width to 100% when screen width >= 1400px
                },
                "@media (max-width: 1500px)": {
                  width: "58%", // Change width to 100% when screen width >= 1400px
                },
                "@media (max-width: 1400px)": {
                  width: "56%", // Change width to 100% when screen width >= 1400px
                },
                "@media (max-width: 1300px)": {
                  width: "49%", // Change width to 100% when screen width >= 1400px
                },
                "@media (max-width: 1200px)": {
                  width: "50%", // Change width to 100% when screen width >= 1400px
                },
                paddingBottom: "4px",
                /* Custom Scrollbar */
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f0f0f0",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(189, 189, 189, 1)",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "rgba(192, 192, 192, 1)",
                },
              }}
            >
              <Box sx={{ flexShrink: 0, minWidth: "250px" }}>
                <Autocomplete
                  data-tracker="300"
                  disablePortal
                  options={Array.from(new Set(requiredInsights?.map((item: any) => item?.data["query_output:sec_title"]?.value)))}
                  onChange={(_, newValue) => {
                    dispatch(setSelectedBusinessInsight(newValue));
                    dispatch(setSelectedRows([])); // Clear selected rows when search changes
                    setSearchParams((prevParams) => {
                      const updatedParams = Object.fromEntries(prevParams); // Preserve previous params
                      if (newValue) {
                        //@ts-ignore
                        updatedParams.search = newValue; // Set new value
                        updatedParams.page = "0";
                      } else {
                        delete updatedParams.search; // Remove from URL if cleared
                      }
                      return updatedParams;
                    });
                  }}
                  value={selectedBusinessInsight}
                  renderInput={(params) => (
                    <Tooltip title={selectedBusinessInsight || ""} arrow placement="top">
                      <TextField
                        {...params}
                        label="Insight Name"
                        size="small"
                        color="secondary"
                        data-tracker="300"
                        sx={{ width: "300px" }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ marginLeft: "12px" }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Tooltip>
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Tooltip title={option || ""} arrow placement="right">
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", maxWidth: 250 }}>
                          {option}
                        </span>
                      </Tooltip>
                    </li>
                  )}
                  sx={{ paddingTop: "8px" }}
                />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ paddingTop: "8px" }}
                >
                  <InsightsFiltersCard clearSelectedBusinessInsights={clearSelectedBusinessInsights} />
                </Stack>
              </Box>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </>
  );
};

export default InsightsFilters;
